export const SUPPORTED_LOCALES = ['vi', 'en'] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'vi';
export const LOCALE_COOKIE_NAME = 'freedraw-locale';

export function isSupportedLocale(value: string | null | undefined): value is AppLocale {
  return SUPPORTED_LOCALES.includes(value as AppLocale);
}

export function resolveLocale(value: string | null | undefined): AppLocale {
  if (isSupportedLocale(value)) {
    return value;
  }

  return DEFAULT_LOCALE;
}

export function detectLocaleFromAcceptLanguage(
  headerValue: string | null | undefined
): AppLocale {
  if (!headerValue) {
    return DEFAULT_LOCALE;
  }

  const tokens = headerValue
    .split(',')
    .map((part) => part.trim().split(';')[0]?.trim().toLowerCase())
    .filter((token): token is string => Boolean(token));

  for (const token of tokens) {
    if (token === 'vi' || token.startsWith('vi-')) {
      return 'vi';
    }
    if (token === 'en' || token.startsWith('en-')) {
      return 'en';
    }
  }

  return DEFAULT_LOCALE;
}

export function getFreedrawLangCode(locale: AppLocale): 'en' | 'vi-VN' {
  return locale === 'en' ? 'en' : 'vi-VN';
}

export function stripLocalePrefix(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const normalized = pathname.replace(/^\/(vi|en)(?=\/|$)/, '') || '/';
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

export function buildLocaleHref(
  pathname: string,
  search: string,
  nextLocale: AppLocale
): string {
  const normalizedPath = stripLocalePrefix(pathname);
  const suffix = normalizedPath === '/' ? '' : normalizedPath;
  const normalizedSearch = search.startsWith('?') || search.length === 0 ? search : `?${search}`;
  return `/${nextLocale}${suffix}${normalizedSearch}`;
}

// Signed-in entry point: resolves to the user's latest board (the public landing page lives at "/").
export const APP_ENTRY_PATH = '/app';

export function buildBoardPath(roomId: string, search = ''): string {
  const normalizedSearch = search.startsWith('?') || search.length === 0 ? search : `?${search}`;

  if (roomId === 'default-room') {
    return `${APP_ENTRY_PATH}${normalizedSearch}`;
  }

  return `/board/${encodeURIComponent(roomId)}${normalizedSearch}`;
}
